import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { AuthApiService } from '../../core/api-services/auth-api.service';
import { BRAND_ASSETS } from '../../core/constants/brand-assets';

@Component({
  selector: 'app-verify-2fa',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './verify-2fa.component.html',
  styleUrl: './verify-2fa.component.scss'
})
export class Verify2faComponent implements OnInit {
  readonly logo = BRAND_ASSETS.logoShieldOnDark;
  verifyForm: FormGroup;
  loading = false;
  errorMessage = '';
  userEmail = '';
  useBackupCode = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private authApi: AuthApiService
  ) {
    this.verifyForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit(): void {
    // Get email from route state (passed from login page)
    const navigation = this.router.getCurrentNavigation();
    this.userEmail = navigation?.extras?.state?.['email'] || '';

    if (!this.userEmail) {
      // If no email in state, redirect back to login
      this.router.navigate(['/login']);
    }
  }

  toggleBackupCode(): void {
    this.useBackupCode = !this.useBackupCode;
    this.errorMessage = '';
    this.verifyForm.reset();

    // Update validators based on mode
    if (this.useBackupCode) {
      this.verifyForm.controls['code'].setValidators([Validators.required, Validators.minLength(8), Validators.maxLength(8)]);
    } else {
      this.verifyForm.controls['code'].setValidators([Validators.required, Validators.minLength(6)]);
    }
    this.verifyForm.controls['code'].updateValueAndValidity();
  }

  async onSubmit(): Promise<void> {
    if (this.verifyForm.invalid) {
      this.verifyForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      const { code } = this.verifyForm.value;

      // Call backend 2FA verification endpoint
      // A wrong code fails with a 401 ApiError (handled below), so reaching here means it verified.
      await firstValueFrom(this.authApi.verifyTwoFactor(code.trim(), this.userEmail));
      this.router.navigate(['/dashboard']);
    } catch (err: unknown) {
      console.error('2FA verification error:', err);
      const error = ApiError.fromHttpError(err);

      if (error.status === 401) {
        this.errorMessage = 'Invalid verification code. Please try again.';
      } else if (error.status === 400) {
        this.errorMessage = error.errors[0] ?? '2FA is not enabled for this account.';
      } else {
        this.errorMessage = 'An error occurred during verification. Please try again.';
      }
    } finally {
      this.loading = false;
    }
  }

  goBackToLogin(): void {
    this.router.navigate(['/login']);
  }
}
